import React from 'react';
import { Printer } from 'lucide-react';
import PRINT_CSS from './printStyles';

const esc = (s = '') => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/**
 * Core printer: writes a COMPLETE standalone document into a hidden iframe
 * and prints it once images/fonts are ready. Because all CSS is embedded,
 * the output can never come out blank due to app styles failing to load.
 */
export function printHTML(html, title = 'Print') {
  if (typeof window === 'undefined') return;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = win.document;
  doc.open();
  doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>` +
    `<style>${PRINT_CSS}</style></head><body>${html}</body></html>`);
  doc.close();

  // Wait until every image has loaded (logos!) before opening the dialog
  const imgsReady = Promise.all(
    Array.from(doc.images).map((img) =>
      img.complete ? Promise.resolve() : new Promise((r) => { img.onload = r; img.onerror = r; }))
  );
  const fontsReady = doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve();
  const timeout = new Promise((r) => setTimeout(r, 2500)); // never hang the dialog

  Promise.race([Promise.all([imgsReady, fontsReady]), timeout]).then(() => {
    setTimeout(() => {
      try { win.focus(); win.print(); } catch (e) { /* ignore */ }
    }, 150);
  });

  // Clean up long after any spooling has finished
  setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 120000);
}

/**
 * Print a DOM node (React ref target) as a full A4 page.
 * The node's markup is paired with the standalone QBO-style stylesheet,
 * so printed documents are independent of screen/app CSS.
 */
export function printNode(node, title = 'Print') {
  if (!node) return;
  const html = node.outerHTML || node.innerHTML;
  printHTML(html, title);
}

/**
 * A small toolbar button that prints the element referenced by `targetRef`
 * (a React ref) or a CSS selector string passed as `target`.
 */
export default function PrintButton({ targetRef, target, title, label = 'Print', className = 'btn btn-outline btn-sm', size = 14 }) {
  const handlePrint = () => {
    const el = targetRef && targetRef.current ? targetRef.current : (typeof target === 'string' ? document.querySelector(target) : null);
    printNode(el, title);
  };
  return (
    <button type="button" className={className} onClick={handlePrint}>
      <Printer size={size} style={{ verticalAlign: '-2px', marginRight: 4 }} /> {label}
    </button>
  );
}
